import type { FC } from "react";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import { createNewRowForMultiFilter } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { getDropdownOptions } from "app/components/multi-include-exclude-filter/utils";
import type { ICategoryItem } from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";
import { SHOPPING, TEXT } from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";

import {
    ORDER_BY,
    INCLUDE_EXCLUDE_KEYWORDS,
    INCLUDE_EXCLUDE_URL,
    AD_TYPE,
    INCLUDE_EXCLUDE_TITLE,
    SELECTED_CATEGORY,
    DOMAINS,
} from "./components/consts";
import { GenericDropdownFilter } from "./components/GenericDropdownFilter";
import { MultiIncludeExcludeDashboardFilter } from "./components/MultiIncludeExcludeDashboardFilter";
import { MultiSelectCategoriesFilter } from "./components/MultiSelectCategoriesFilter";
import { DomainsDashboardFilter } from "./components/DomainsDashboardFilter";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { swSettings } from "app/common/services/swSettings";

const KEY = "dynamicFiltersContainer";
const orderByOptions = [
    { id: "clicksShare desc", text: "Clicks Share" },
    { id: "volume desc", text: "Volume" },
];

const adTypeOptions = [
    { id: TEXT, textKey: "keyword.analysis.ads.type.text" },
    { id: SHOPPING.toLowerCase(), textKey: "keyword.analysis.ads.type.shopping" },
];

interface IFilterObject {
    [AD_TYPE]: string;
    [ORDER_BY]: string;
    [INCLUDE_EXCLUDE_KEYWORDS]: string;
    [INCLUDE_EXCLUDE_URL]: string;
    [INCLUDE_EXCLUDE_TITLE]: string;
    [SELECTED_CATEGORY]: string;
    [DOMAINS]: string;
}

export const SearchAdsTableWidgetFilters: FC<any> = (props) => {
    const {
        widgetFilters: filterObject,
        changeFilter: onFilterChangeCallback,
        widgetMetric,
        widgetPreview,
        widgetKeys,
    } = props;
    const isWebsiteMetric = widgetMetric === "SearchAdsWebsite";

    const onFilterChange = (filterName: keyof IFilterObject, filterValue: string) => {
        const newData = { ...filterObject, [filterName]: filterValue };

        const updatedFilters = Object.entries(newData).map(([name, value]) => ({
            name,
            value,
        }));

        onFilterChangeCallback(updatedFilters);
    };

    const handleCategoryChange = (selectedCategories: ICategoryItem[]) => {
        const value = selectedCategories.length > 0 ? selectedCategories.map((cat) => cat.id).join(",") : null;
        onFilterChange(SELECTED_CATEGORY, value);
    };

    const handleDomainsChange = (item) => {
        onFilterChange(DOMAINS, item);
    };

    const handleMultiIncludeExcludeChange = (rules: IDynamicFilterRule[], filterName: keyof IFilterObject) => {
        const rulesWithItems = rules.filter((rule) => rule.items.length > 0);
        onFilterChange(filterName, JSON.stringify(rulesWithItems));
    };

    const emptyRule = [createNewRowForMultiFilter(getDropdownOptions(multiIncludeExcludeType.KEYWORD))];

    let selectedKeywords: IDynamicFilterRule[] = emptyRule;
    let selectedWebsites: IDynamicFilterRule[] = emptyRule;
    let selectedTitles: IDynamicFilterRule[] = emptyRule;
    try {
        if (filterObject.multiIncludeExcludeKeywords) {
            selectedKeywords = JSON.parse(filterObject.multiIncludeExcludeKeywords);
        }
        if (filterObject.multiIncludeExcludeUrl) {
            selectedWebsites = JSON.parse(filterObject.multiIncludeExcludeUrl);
        }
        if (filterObject.multiIncludeExcludeTitle) {
            selectedTitles = JSON.parse(filterObject.multiIncludeExcludeTitle);
        }
    } catch (e) {
        Sentry.error("Error parsing multiIncludeExclude filters", e);
    }

    return (
        <div
            key={KEY}
            className="filters"
        >
            <GenericDropdownFilter
                titleKey={AD_TYPE}
                items={adTypeOptions.map((option) => ({ ...option, text: translate(option.textKey) }))}
                selectedId={filterObject[AD_TYPE] ?? adTypeOptions[0].id}
                onSelect={(value) => onFilterChange(AD_TYPE, value.id)}
            />
            <GenericDropdownFilter
                titleKey={ORDER_BY}
                items={orderByOptions}
                selectedId={filterObject[ORDER_BY] ?? orderByOptions[0].id}
                onSelect={(value) => onFilterChange(ORDER_BY, value.id)}
            />
            {!isWebsiteMetric ? (
                <MultiSelectCategoriesFilter
                    onApprove={handleCategoryChange}
                    selectedCategories={filterObject[SELECTED_CATEGORY]}
                />
            ) : null}
            {!isWebsiteMetric && swSettings.components.Home.resources.HasUnicornsCanary ? (
                <div className="dynamicFilter">
                    <I18n component="h5">keywords.analysis.filters.domains.projects.button</I18n>
                    <DomainsDashboardFilter
                        domainsList={widgetPreview?.Filters}
                        domains={filterObject?.domains}
                        onSelect={handleDomainsChange}
                        keyword={widgetKeys[0]?.id}
                    />
                </div>
            ) : null}
            {isWebsiteMetric ? (
                <MultiIncludeExcludeDashboardFilter
                    filterType={multiIncludeExcludeType.KEYWORD}
                    initialRules={selectedKeywords}
                    onMultiIncludeExcludeChange={(rules) =>
                        handleMultiIncludeExcludeChange(rules, INCLUDE_EXCLUDE_KEYWORDS)
                    }
                />
            ) : null}

            <MultiIncludeExcludeDashboardFilter
                filterType={multiIncludeExcludeType.URL}
                initialRules={selectedWebsites}
                onMultiIncludeExcludeChange={(rules) => handleMultiIncludeExcludeChange(rules, INCLUDE_EXCLUDE_URL)}
            />
            {!isWebsiteMetric ? (
                <MultiIncludeExcludeDashboardFilter
                    filterType={multiIncludeExcludeType.TITLE}
                    initialRules={selectedTitles}
                    onMultiIncludeExcludeChange={(rules) =>
                        handleMultiIncludeExcludeChange(rules, INCLUDE_EXCLUDE_TITLE)
                    }
                />
            ) : null}
        </div>
    );
};
